import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const DEV_PHASES = [
  {
    status: "done",
    title: "Product vision & UX",
    detail: "Goals, accessibility-minded layouts, and offline-first flows defined.",
  },
  {
    status: "done",
    title: "Offline sales, expenses & stock",
    detail: "Core recording and local storage so everyday shop work never depends on signal.",
  },
  {
    status: "done",
    title: "Secure cloud sync",
    detail: "Background sync when connectivity returns, with clear conflict-safe merges.",
  },
  {
    status: "planned",
    title: "Pilot with rural shops",
    detail: "Real-world feedback loop before a wider release.",
  },
];

/** Inline art so the hero always renders (no external URLs; prior Unsplash link returned 404). */
function StorePalHeroIllustration({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 800 520"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Neighborhood shop with a tablet register and cloud sync"
    >
      <defs>
        <linearGradient id="spHeroBg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1d4ed8" />
          <stop offset="100%" stopColor="#1e3a8a" />
        </linearGradient>
        <linearGradient id="spAwning" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#93c5fd" />
          <stop offset="100%" stopColor="#60a5fa" />
        </linearGradient>
        <filter id="spSoftShadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#000" floodOpacity="0.25" />
        </filter>
      </defs>
      <rect width="800" height="520" fill="url(#spHeroBg)" />
      {/* ground */}
      <ellipse cx="400" cy="480" rx="340" ry="36" fill="#172554" opacity="0.35" />
      {/* shop */}
      <g filter="url(#spSoftShadow)">
        <rect x="180" y="160" width="360" height="260" rx="12" fill="#eff6ff" />
        <rect x="196" y="176" width="120" height="100" rx="6" fill="#bfdbfe" opacity="0.9" />
        <rect x="336" y="176" width="120" height="100" rx="6" fill="#bfdbfe" opacity="0.9" />
        <rect x="476" y="176" width="48" height="228" rx="6" fill="#334155" />
        <circle cx="502" cy="360" r="6" fill="#fbbf24" />
        <path d="M150 200 L430 200 L410 250 L170 250 Z" fill="url(#spAwning)" />
        <path
          d="M170 250 h300 v2 h-20 l-8 12 h-244 Z"
          fill="#1e40af"
          opacity="0.9"
        />
      </g>
      {/* counter + tablet POS */}
      <rect x="220" y="310" width="200" height="16" rx="4" fill="#cbd5e1" />
      <rect x="260" y="250" width="120" height="72" rx="8" fill="#1e293b" stroke="#64748b" strokeWidth="3" />
      <rect x="272" y="262" width="96" height="48" rx="4" fill="#38bdf8" opacity="0.85" />
      <text x="320" y="294" textAnchor="middle" fill="#0c4a6e" fontSize="22" fontFamily="system-ui, sans-serif" fontWeight="700">
        ₦
      </text>
      {/* person (simple) */}
      <circle cx="520" cy="320" r="28" fill="#fed7aa" />
      <path d="M520 352 v56 h-40 v-12 h80 v12 h-40 Z" fill="#1d4ed8" />
      {/* cloud sync */}
      <g opacity="0.95">
        <path
          d="M580 88 c-20 0-36 14-40 32-24 4-42 26-42 52 0 29 23 52 52 52 h168 c27 0 48-22 48-50 0-26-20-48-46-52-6-34-36-60-72-60-28 0-52 16-64 38-4-1-8-2-12-2z"
          fill="#e0f2fe"
        />
        <path
          d="M620 128 L632 152 L656 152 L638 168 L646 192 L620 178 L594 192 L602 168 L584 152 L608 152 Z"
          fill="#fbbf24"
          opacity="0.9"
        />
      </g>
      <path
        d="M640 200 Q680 220 660 260"
        fill="none"
        stroke="#93c5fd"
        strokeWidth="4"
        strokeLinecap="round"
        opacity="0.8"
      />
      <path
        d="M620 268 L652 296 M652 268 L620 296"
        stroke="#93c5fd"
        strokeWidth="5"
        strokeLinecap="round"
        opacity="0.75"
      />
    </svg>
  );
}

function StatusIcon({ status }) {
  if (status === "done") {
    return (
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700 ring-2 ring-green-200">
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </span>
    );
  }
  if (status === "active") {
    return (
      <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-800 ring-2 ring-blue-300">
        <motion.span
          className="absolute inset-0 rounded-full bg-blue-400/30"
          animate={{ scale: [1, 1.35, 1], opacity: [0.55, 0, 0.55] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          aria-hidden
        />
        <span className="relative text-xs font-bold uppercase tracking-wide">Now</span>
      </span>
    );
  }
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 ring-2 ring-gray-200">
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </span>
  );
}

function statusLabel(status) {
  if (status === "done") return "Complete";
  if (status === "active") return "In progress";
  return "Planned";
}

export default function StorePalPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 text-gray-900 font-sans antialiased">
      <header className="bg-gradient-to-r from-blue-900 to-blue-700 text-white text-center py-12 shadow-xl relative overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 max-w-3xl mx-auto px-4"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm underline-offset-4 text-white/85 hover:text-white transition mb-8"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to HunterFortress
          </Link>
          <img
            src="/HunterFortress_Logo_Shield_Crosshairs_Optimized.png"
            alt="HunterFortress Logo"
            className="mx-auto h-20 mb-6 drop-shadow-lg"
          />
          <motion.p
            className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-200 mb-3"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            Product
          </motion.p>
          <motion.h1
            className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, type: "spring" }}
          >
            StorePal
          </motion.h1>
          <motion.p
            className="text-lg sm:text-xl font-light max-w-2xl mx-auto opacity-95 leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35 }}
          >
            Point-of-sale that works when the network does not — built for small shops and steady books.
          </motion.p>
        </motion.div>
        <motion.div
          className="absolute top-1/2 left-1/2 w-72 h-72 rounded-full border-2 border-blue-300/30 pointer-events-none"
          animate={{ scale: [1, 1.4, 1.8], opacity: [0.25, 0.08, 0] }}
          transition={{ repeat: Infinity, duration: 4, ease: "easeOut" }}
          aria-hidden
        />
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-16 space-y-14">
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="overflow-hidden rounded-2xl shadow-xl border border-blue-100 bg-white"
        >
          <div className="grid md:grid-cols-2 gap-0">
            <div className="relative min-h-[220px] md:min-h-[320px] bg-gradient-to-br from-blue-800 to-blue-600 overflow-hidden">
              <StorePalHeroIllustration className="absolute inset-0 h-full w-full min-h-[220px] md:min-h-[320px]" />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/85 via-blue-900/15 to-transparent md:bg-gradient-to-r md:from-blue-900/25 md:via-transparent md:to-blue-900/40 pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 md:bottom-8 md:left-8 text-white drop-shadow-md">
                <p className="text-sm font-medium text-blue-100">Offline first, human first</p>
                <p className="text-xl font-bold leading-snug">Clear records — with or without Wi‑Fi</p>
              </div>
            </div>
            <div className="p-8 flex flex-col justify-center">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">A simple intro</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                StorePal is a point-of-sale app for people who run a small shop in places where the internet is unreliable.
                It works offline first: you can record sales, expenses, and stock without a connection; when you are back online,
                everything syncs safely to the cloud so your numbers stay up to date.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The idea is simple tools, big buttons, and no pressure to always be connected — so shopkeepers can focus on
                customers, not on tech problems.
              </p>
            </div>
          </div>
        </motion.section>

        <motion.section
          className="bg-white p-8 rounded-xl shadow-lg border border-blue-100"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-blue-900 mb-6 flex items-center">
            <span className="bg-blue-100 p-2 rounded-full mr-3">🌾</span>
            Who we are designing for
          </h2>
          <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
            <p>
              We designed StorePal with older citizens and rural communities in mind — people who may not have fast data or Wi‑Fi every day,
              but still need clear records of what they sell, what they spend, and what they have in stock.
            </p>
            <p className="font-medium text-blue-900 border-l-4 border-blue-500 pl-4 py-1">
              In short: StorePal helps shopkeepers in rural areas keep the books even when the network fails.
            </p>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">
            <span className="border-b-4 border-blue-500 pb-2">What you can do with StorePal</span>
          </h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {[
              { icon: "📴", title: "Work fully offline", body: "Record sales, costs, and stock without waiting on signal." },
              { icon: "☁️", title: "Sync when you are online", body: "Updates merge to the cloud securely when connectivity is back." },
              { icon: "📊", title: "Books that stay legible", body: "Straightforward summaries so nothing gets lost in the noise." },
              { icon: "👆", title: "Big, calm interface", body: "Fewer steps, larger targets — less strain, fewer mistakes." },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:border-blue-200 transition-colors"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                viewport={{ once: true }}
              >
                <div className="text-3xl mb-3" aria-hidden>
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-blue-800 mb-2">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section
          className="bg-blue-900 text-white p-8 rounded-xl shadow-lg"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <h2 className="text-3xl font-bold flex items-center">
              <span className="bg-white text-blue-900 p-2 rounded-full mr-3 text-lg">📍</span>
              Development status
            </h2>
            <p className="text-blue-200 text-sm sm:text-base max-w-md">
              Live snapshot of where we are — refreshed as milestones land.
            </p>
          </div>
          <ul className="space-y-0">
            {DEV_PHASES.map((phase, index) => (
              <li key={phase.title} className="flex gap-4 sm:gap-6">
                <div className="flex flex-col items-center pt-1">
                  <StatusIcon status={phase.status} />
                  {index < DEV_PHASES.length - 1 && (
                    <span className="w-0.5 flex-1 min-h-[1.25rem] bg-blue-700/80 my-1 rounded" aria-hidden />
                  )}
                </div>
                <div className={`pb-8 ${index === DEV_PHASES.length - 1 ? "pb-0" : ""} flex-1`}>
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="text-lg font-bold">{phase.title}</h3>
                    <span
                      className={`text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                        phase.status === "done"
                          ? "bg-green-500/20 text-green-200"
                          : phase.status === "active"
                            ? "bg-amber-400/25 text-amber-100"
                            : "bg-white/10 text-blue-200"
                      }`}
                    >
                      {statusLabel(phase.status)}
                    </span>
                  </div>
                  <p className="text-blue-100/90 leading-relaxed">{phase.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </motion.section>

        <motion.section
          className="text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <p className="text-gray-600 mb-4">Questions about StorePal or partnerships?</p>
          <a
            href="mailto:security@hunterfortress.com?subject=StorePal%20inquiry"
            className="inline-flex items-center gap-2 text-blue-700 font-semibold hover:text-blue-900 transition"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Get in touch
          </a>
        </motion.section>
      </main>

      <footer className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-8 mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center mb-4 md:mb-0">
            <img src="/HunterFortress_Logo_Shield_Crosshairs_Optimized.png" alt="Logo" className="h-10 mr-3" />
            <span className="text-xl font-bold">HunterFortress</span>
          </div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <Link to="/" className="hover:text-blue-300 transition font-semibold">
              Home
            </Link>
            <Link to="/contact" className="hover:text-blue-300 transition font-semibold">
              Contact
            </Link>
            <Link to="/storepal" className="hover:text-blue-300 transition text-blue-200 font-semibold">
              StorePal
            </Link>
            <Link to="/terms" className="hover:text-blue-300 transition font-semibold">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-blue-300 transition">
              Privacy
            </Link>
            <Link to="/blog" className="hover:text-blue-300 transition">
              Blog
            </Link>
          </div>
        </div>
        <div className="text-center mt-6 pt-6 border-t border-blue-700">
          <p>&copy; 2025 HunterFortress. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
